import type { IdeSession } from "~/api/opsTypes";

/**
 * The four rules that decide what a connected editor window's badges say.
 *
 * They are pure, they are unit-tested, and they are transferred from kms behaviour-for-
 * behaviour rather than re-derived — each one has a way of being got subtly wrong that no
 * type can catch: a version compare that reads "0.5.9" as newer than "0.5.10", a binding chip
 * that resolves a foreign team id to a name, a policy reader that assumes silence means "off".
 */

export type BindingChipState = "unbound" | "bound" | "own-workspace" | "mismatch";

/**
 * The first extension whose documentation sync and flow mirror ride a token scoped to the
 * repo's OWN workspace (`auth.workspaceToken`), instead of the one sign-in every VS Code
 * window on the machine shares.
 */
export const SCOPED_BINDING_SINCE = "0.6.19";

/**
 * Four states. The server's `bindingMismatch` only says the folder's workspace and the
 * sign-in's workspace DIFFER — it stopped meaning "refused" on 2026-09-27:
 *
 * - `own-workspace`: the window's sign-in is in one workspace and its folder is bound to
 *   another, and the extension is new enough to sync the folder into its own workspace with a
 *   token of its own. Nothing is refused; this is the normal shape of two repos in two
 *   workspaces on one machine, because the sign-in is shared and only one of them can match it.
 * - `mismatch`: the same difference on an extension that predates that (its sync IS refused
 *   server-side, `409 binding_mismatch`), or a folder bound to a workspace its owner does not
 *   belong to (no token can be minted for it). This chip is how a person learns why their
 *   documentation stopped arriving.
 *
 * `ownerBelongsToBound` is passed only where it is known (the person's own windows against
 * their own workspace list); `undefined` leaves the extension version to decide.
 *
 * Only the first 8 characters of the foreign team id are ever shown, and the caller must
 * never resolve it to a name — that would be a cross-tenant leak dressed as a helpful label.
 */
export function bindingChipState(
  session: Pick<IdeSession, "boundTeamId" | "bindingMismatch" | "extensionVersion">,
  ownerBelongsToBound?: boolean,
): BindingChipState {
  if (!session.boundTeamId) {
    return "unbound";
  }
  if (session.bindingMismatch !== true) {
    return "bound";
  }
  if (ownerBelongsToBound === false) {
    return "mismatch";
  }
  return isExtensionVersionBehind(session.extensionVersion, SCOPED_BINDING_SINCE)
    ? "mismatch"
    : "own-workspace";
}

/**
 * A numeric dotted compare, missing segments counted as zero.
 *
 * Two consequences the string compare a rewrite reaches for gets wrong: `"0.5.9"` IS behind
 * `"0.5.10"` (9 < 10, though "9" > "1" as text), and an UNSET version is always behind a set
 * advised version — a window that never declared one is exactly the old window the advice is
 * aimed at. A server with no opinion (`advised === null`) leaves every window current.
 */
export function isExtensionVersionBehind(
  version: string | null | undefined,
  advised: string | null | undefined,
): boolean {
  if (!advised) {
    return false;
  }
  if (!version) {
    return true;
  }
  const parts = (value: string): number[] =>
    value
      .split(".")
      .map((segment) => Number.parseInt(segment.replace(/[^0-9]/g, ""), 10))
      .map((segment) => (Number.isFinite(segment) ? segment : 0));

  const left = parts(version);
  const right = parts(advised);
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const a = left[index] ?? 0;
    const b = right[index] ?? 0;
    if (a !== b) {
      return a < b;
    }
  }
  return false;
}

export type InboundPolicy = "off" | "ask" | "allow" | "unknown";

/**
 * The window's declared inbound policy, read off its capability list.
 *
 * `unknown` is a real answer, not a fallback for a broken read: a session key is minted per
 * ACTIVATION, so a window that has not been reloaded since the feature shipped keeps its old
 * capability set and genuinely has not said. That is why the console explains it rather than
 * defaulting to `ask` — a prompt sent to a window that predates delegation may never be
 * claimed at all, and guessing "ask" would hide that.
 */
export function inboundPolicyOf(capabilities: readonly string[] | null | undefined): InboundPolicy {
  if (!Array.isArray(capabilities)) {
    return "unknown";
  }
  const prefix = "ide.inbound.";
  const declared = capabilities.find(
    (capability) => typeof capability === "string" && capability.startsWith(prefix),
  );
  const value = declared?.slice(prefix.length);
  return value === "off" || value === "ask" || value === "allow" ? value : "unknown";
}

/** The clone's folder, for a table cell too narrow for an absolute path. */
export function workspaceBasename(path: string | null | undefined): string {
  if (!path) {
    return "";
  }
  const trimmed = path.replace(/[\\/]+$/, "");
  const index = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  return index === -1 ? trimmed : trimmed.slice(index + 1);
}

/** kms's own web-side threshold, independent of the extension's `docit.docSync.staleWarnDays`. */
export const STALE_WARN_DAYS = 3;

/**
 * A clone is stale when its NEWEST activity is older than three days — **or when it has
 * none at all**, which is the shape of the 2026-08-09 incident: a clone whose local config
 * lost its `knowItSync` block starts no watcher, pushes nothing, and therefore has no
 * timestamp to be old. Treating "never" as "not yet stale" would hide precisely the failure
 * this warning exists for.
 */
export function isStaleClient(
  client: { lastPushAt: string | null; lastPullAt: string | null },
  now: number = Date.now(),
): boolean {
  const times = [client.lastPushAt, client.lastPullAt]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .map((value) => Date.parse(value))
    .filter((value) => Number.isFinite(value));

  if (times.length === 0) {
    return true;
  }
  return now - Math.max(...times) > STALE_WARN_DAYS * 24 * 60 * 60 * 1000;
}

/** Whole days since a timestamp, for the stale band's "{{n}} days" — floor, never round up. */
export function daysSince(iso: string | null, now: number = Date.now()): number {
  if (!iso) {
    return 0;
  }
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) {
    return 0;
  }
  return Math.max(0, Math.floor((now - then) / (24 * 60 * 60 * 1000)));
}

/**
 * Online first, then most-recently-seen. Sorting by `lastSeenAt` alone would bury a window
 * that is live right now under a dead one somebody used more recently.
 */
export function orderSessions(sessions: readonly IdeSession[]): IdeSession[] {
  return [...sessions].sort((a, b) => {
    if (a.isOnline !== b.isOnline) {
      return a.isOnline ? -1 : 1;
    }
    return Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt);
  });
}
